import type { Result } from 'neverthrow';
import type { DatasetOperationV20260601 } from '@cotera/api';
import type { DuckDbLayerGrid } from '@cotera/client/app/etc/duckdb/layers';
import { datasetOperationPatches } from './operation-patches';
import type { DatasetPreviewColumn } from './operations';
import type { DatasetPreviewLayer } from './types';

/**
 * Only what the sync reads. Narrower than the typed client's own signature on
 * purpose: this asks for one thing — the operations past sequence N — and a type
 * naming every field of the response would suggest the rest of it mattered.
 */
export type LiveOperationsClient = {
  resource: {
    dataset: {
      listOperations: (params: {
        id: string;
        afterSequenceNumber?: number;
      }) => Promise<
        Result<
          {
            readonly operations: readonly {
              readonly sequenceNumber: number;
              readonly payload: DatasetOperationV20260601;
            }[];
          },
          unknown
        >
      >;
    };
  };
};

/**
 * Only what the sync writes through. Narrow so a spec can hand over an object
 * literal with one `vi.fn()` rather than standing up DuckDB-wasm.
 */
export type LiveOperationsTable = {
  apply: (input: {
    operations: readonly DatasetOperationV20260601[];
    columns: readonly DatasetPreviewColumn[];
  }) => Promise<void>;
};

/** What the grid is currently showing, which arrives long after construction. */
export type DatasetLiveOperationsSource = {
  readonly datasetId: string;
  /**
   * The artifact the preview is keyed on. Identity only — changing it means the
   * table was rebuilt from a different parquet, so nothing applied to the old
   * one counts any more.
   */
  readonly artifactId: string;
  /**
   * The materialized table, or null until its initial replay has finished.
   *
   * Handed over rather than opened here, because "a table for this artifact"
   * and "a table that exists" are different claims and only the caller can make
   * the second. Applying an `UPDATE` to a table a rebuild has not created yet
   * fails outright, and on an artifact change there is a window where that is
   * exactly what the name would resolve to.
   */
  readonly table: LiveOperationsTable | null;
  /** The schema the table has now — after the initial replay, not the parquet's. */
  readonly columns: readonly DatasetPreviewColumn[];
  readonly rowIdColumn: string | null;
};

/**
 * Brings writes that happened elsewhere into an open grid, a cell at a time.
 *
 * A dataset can be written by things that are not this browser tab — a workflow
 * step, Coco, another user. Those land as operations immediately, but the rows
 * they describe do not move until compaction publishes a new artifact, which is
 * deliberately delayed by minutes. Until then an open grid is replaying an
 * operation log it read once, so the write is simply invisible: not lost, but
 * absent for long enough to read as lost.
 *
 * What makes this worth its own object rather than a refetch is what a refetch
 * costs. Rebuilding the preview re-downloads the parquet, replays the whole log
 * onto a fresh table and hands back a new column array — which rebuilds the
 * adapter, and with it the entire grid view model: sorts, filters, selection,
 * expansion and scroll position all gone. For one cell changing. So this applies
 * the increment to the table and patches the affected rows in place instead,
 * exactly as the workflows column does for run state, and falls back to the
 * rebuild only for the operations that genuinely cannot be patched.
 */
export class DatasetLiveOperations {
  /**
   * The grid seam, as a layer. Stable for the lifetime of this object — the
   * grid's view model is memoized on layer identity, so a fresh layer would
   * reset everything this exists to preserve.
   */
  readonly layer: DatasetPreviewLayer = {
    id: 'dataset-live-operations',
    present: () => ({ subscribe: this.registerGrid }),
  };

  private readonly client: LiveOperationsClient;
  private readonly onSchemaChanged: () => void;
  private grid: DuckDbLayerGrid | null = null;
  private source: DatasetLiveOperationsSource | null = null;
  /**
   * The highest sequence number already reflected in the table, from the
   * from-scratch replay or from an earlier sync.
   *
   * -1 rather than 0 because sequence numbers are non-negative, so 0 is a real
   * operation that must not be skipped.
   */
  private appliedThrough = -1;
  /** In-flight sync, so a burst of events costs one pass rather than one each. */
  private running: Promise<void> | null = null;
  private queued = false;
  /**
   * A sync that arrived before there was a table to apply it to.
   *
   * There is a real window here, not just a race with nothing in it: the
   * materialization reads the operation log and marks what it is about to
   * replay, and only exposes the table some time later. An operation written
   * inside that gap is in neither — not in the replay, which read the log before
   * it existed, and not in the sync, which had nowhere to put it. Dropping the
   * request outright leaves the grid stale until the next unrelated event or the
   * next compaction, so it is remembered and run when the table arrives.
   */
  private syncWhenReady = false;

  constructor(params: {
    client: LiveOperationsClient;
    /**
     * Called when something arrived that no patch can express — a column added,
     * renamed or dropped. The caller re-materializes; this deliberately does not
     * do it itself, because the rebuild is the caller's effect to own.
     */
    onSchemaChanged: () => void;
  }) {
    this.client = params.client;
    this.onSchemaChanged = params.onSchemaChanged;
  }

  /**
   * Points this at what the grid is showing. Idempotent by content, so the page
   * can call it on every render.
   *
   * Moving to a different dataset or artifact resets the high-water mark: the
   * new table was built by its own replay, and a sequence number from the old
   * one says nothing about what this one already holds.
   */
  setSource(source: DatasetLiveOperationsSource | null): void {
    const current = this.source;
    if (
      current !== null &&
      source !== null &&
      current.datasetId === source.datasetId &&
      current.artifactId === source.artifactId
    ) {
      // The schema can change under a stable artifact — the initial replay adds
      // whatever the log added — and later syncs need the current one.
      this.source = source;
      this.flushSyncWhenReady();
      return;
    }
    this.source = source;
    this.appliedThrough = -1;
    this.flushSyncWhenReady();
  }

  /**
   * Runs a sync that had to wait for the table, now that there is one.
   *
   * Safe to run the moment the table appears: the replay that produced it marks
   * its high-water line *before* it runs, so a sync here asks only for what came
   * after — which is exactly the operation the window swallowed.
   */
  private flushSyncWhenReady(): void {
    const table = this.source?.table;
    if (!this.syncWhenReady || table === null || table === undefined) {
      return;
    }
    this.syncWhenReady = false;
    void this.sync();
  }

  /**
   * Records the high-water mark a from-scratch replay just applied.
   *
   * Called by the materialization rather than inferred here, and called *before*
   * that replay runs. Both matter: a sync that starts while a rebuild is in
   * flight would otherwise ask for operations the rebuild is about to replay and
   * apply them a second time — which for an insert means a duplicate row.
   */
  readonly markApplied = (sequenceNumbers: readonly number[]): void => {
    this.appliedThrough = sequenceNumbers.reduce(
      (highest, sequenceNumber) => Math.max(highest, sequenceNumber),
      this.appliedThrough
    );
  };

  /**
   * Pulls everything written past the high-water mark and applies it.
   *
   * Serialized against itself, with at most one pass queued behind the one
   * running: each pass reads the mark afresh, so a second event arriving mid
   * flight needs one more pass afterwards, not one per event.
   */
  readonly sync = async (): Promise<void> => {
    if (this.running !== null) {
      this.queued = true;
      return this.running;
    }
    this.running = this.runSync()
      .catch((error: unknown) => {
        // Callers fire this from an event handler and cannot await it, so a
        // rejection here would surface as an unhandled one and nowhere else.
        // Losing a batch is survivable — compaction publishes an artifact that
        // includes it within minutes — but losing it *silently* is not.
        // eslint-disable-next-line no-console
        console.error('[dataset-live-operations] sync threw', error);
      })
      .finally(() => {
        this.running = null;
      });
    await this.running;
    if (this.queued) {
      this.queued = false;
      await this.sync();
    }
  };

  private async runSync(): Promise<void> {
    const source = this.source;
    // No table means the preview has not finished materializing. Its replay
    // covers everything the log held when it read it — but not what was written
    // after that read, so the request is kept and run once the table lands.
    if (source === null || source.table === null) {
      this.syncWhenReady = true;
      return;
    }
    const { datasetId, artifactId } = source;
    const result = await this.client.resource.dataset.listOperations({
      id: datasetId,
      afterSequenceNumber: this.appliedThrough,
    });
    if (result.isErr()) {
      return;
    }
    // Re-read rather than trusting what the source said when this started: a
    // rebuild may have replaced the whole table while the request was in
    // flight, and applying to the old one would write into a table nothing
    // reads. Its replay covers these operations anyway.
    const current = this.source;
    const table = current?.table;
    if (
      current === null ||
      table === null ||
      table === undefined ||
      current.datasetId !== datasetId ||
      current.artifactId !== artifactId
    ) {
      return;
    }

    // Filtered again here, not just asked for in the request. A rebuild that
    // landed while the request was in flight has already replayed some of
    // these, and `update_cell` being idempotent does not make `insert_row` so.
    const unseen = result.value.operations.filter(
      (operation) => operation.sequenceNumber > this.appliedThrough
    );
    if (unseen.length === 0) {
      return;
    }

    const plan = datasetOperationPatches(
      unseen.map((operation) => operation.payload),
      { columns: current.columns, rowIdColumn: current.rowIdColumn }
    );
    if (plan.needsRebuild) {
      // Left unmarked on purpose: the rebuild reads the log from the start and
      // marks what it replayed, and a mark set here would make it look as
      // though these had already been applied if the rebuild never ran.
      this.onSchemaChanged();
      return;
    }

    // Claimed before the await rather than after it, so nothing can ask for
    // these operations again while they are being applied. The cost is that a
    // failed apply drops them until the next rebuild; the alternative is a
    // retry that re-runs the inserts it already succeeded at, and a duplicated
    // row is both worse and silent.
    this.markApplied(unseen.map((operation) => operation.sequenceNumber));

    // The table first, then the rows. The table is what sorting, filtering and
    // the header distributions read, so patching the screen without it would
    // show a value the next sort would contradict.
    await table.apply({
      operations: unseen.map((operation) => operation.payload),
      columns: current.columns,
    });

    const grid = this.grid;
    if (grid === null) {
      // Nothing is mounted to patch. The table is still correct, so whatever
      // mounts next reads the new values on its first query.
      return;
    }
    for (const patch of plan.patches) {
      switch (patch.type) {
        case 'update-cell':
          grid.patch(patch.rowId, { [patch.columnName]: patch.value });
          break;
        case 'insert-row':
          grid.insertRow(patch.row);
          break;
        case 'delete-row':
          grid.deleteRow(patch.rowId);
          break;
      }
    }
  }

  /**
   * The seam the grid registers itself through. An arrow property because the
   * layer hands it over on every `present`, and a fresh function would
   * re-register each time.
   */
  private readonly registerGrid = (grid: DuckDbLayerGrid): (() => void) => {
    this.grid = grid;
    return () => {
      this.grid = null;
    };
  };
}
